import {
  clamp,
  dot,
  exp,
  float,
  length,
  max,
  mix,
  normalize,
  normalWorld,
  positionWorld,
  pow,
  smoothstep,
  uniform,
  vec3,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { AtmosphereDef } from '../atmosphere/AtmosphereDef';
import type { HollowDef } from '../universe/HollowDef';
import { GlowFlares } from './GlowFlares';

type N = any;

/**
 * Day and night for a hollow lit by a central sun. The sun wears a slowly
 * turning "veil" (a shade shell) that darkens part of the sky; its rim is where
 * dawn and dusk happen, so from the ground you see a terminator line sweep
 * across the far side overhead.
 */
export interface VeilDef {
  /** Fraction of the wall in daylight at any moment (0..1). */
  litFraction: number;
  /** Width of the dawn/dusk band, in cosine units (~0.05–0.15). */
  softness: number;
  /** Seconds for one full day at time scale 1. */
  dayLength: number;
  /** Axis the veil turns around (hollow frame). */
  spinAxis: [number, number, number];
  /** Colour of light through the veil's rim (dawn/dusk). */
  duskColor: [number, number, number];
  /** Ambient multiplier on the night side (default: a cool twilight). */
  nightAmbient?: [number, number, number];
  /** Ambient tint in the dawn/dusk band, and how strongly it applies. */
  rimAmbient?: [number, number, number];
  rimAmbientStrength?: number;
}

/**
 * Light that the ground itself gives off (bioluminescence, lava…). In a glow
 * hollow the "veil" is not a shade around a sun but the phase of the glow:
 * at its height the ground shines at `day`, at its depth it dims to `night`.
 * Values are average exitance over the wall (irradiance units).
 */
export interface GlowDef {
  color: [number, number, number];
  day: number;
  night: number;
  /**
   * How it rises and falls (in a world without a sun). 'unified': the whole world brightens
   * and dims at once, a slow breath over the veil's `dayLength` (living light). 'chaotic':
   * every region flares on its own at random times and dies down again (lava: see GlowFlares),
   * and the light the lava throws comes from the lava itself (setLocalGlow). Without it, the
   * glow follows the veil: a day that travels round the world.
   */
  pulse?: 'unified' | 'chaotic';
}

/** The unified glow's breath at day phase `time`: 1 at its height (phase 0), 0 at its depth (½), long at both. */
export function glowBreath(time: number): number {
  const c = 0.5 + 0.5 * Math.cos(time * Math.PI * 2);
  const t = Math.min(1, Math.max(0, (c - 0.12) / 0.76));
  return t * t * (3 - 2 * t);
}

/** While it's bright, the unified glow swells and ebbs a little, all at once: its share, and period (s). */
const RIPPLE = 0.07;
const RIPPLE_PERIOD = 6.5;
/** Seconds of flares already run when a chaotic glow starts, so the lava is busy from the first frame. */
const FLARES_PREROLL = 40;
/** The lava's light on the ground around it, per unit of the emission the panorama sees there. */
const LOCAL_GLOW_GAIN = 1;

const _tmp = new THREE.Vector3();

export class LightRig {
  /** Day phase in [0, 1). */
  time = 0.15;
  timeScale = 1;
  /** The central sun (null in hollows without one). */
  readonly sunLight: THREE.PointLight | null;
  readonly ambient: THREE.AmbientLight;
  /** Hollow centre relative to the camera (refreshed per frame by the owner). */
  readonly centerRel = uniform(new THREE.Vector3());
  readonly veilAxis = uniform(new THREE.Vector3(1, 0, 0));
  readonly veilCos = uniform(0);
  readonly veilSoft = uniform(0.08);
  readonly sunColor = uniform(new THREE.Color(1, 1, 1));
  readonly duskColor = uniform(new THREE.Color(1, 0.5, 0.25));
  /** Integrating-sphere ambient irradiance (CPU-updated). */
  readonly ambientColor = uniform(new THREE.Color(0.3, 0.3, 0.3));
  /**
   * Scales the sun and bounce light as the scene's materials see them (1 on, 0 off). They have
   * colour nodes, so their `intensity` does nothing; HollowScene.setActive dims them here.
   */
  readonly dimmer = uniform(1);
  /** Direct irradiance at the wall at noon, before atmospheric absorption. */
  readonly sunIrradiance: number;
  private readonly spin = new THREE.Vector3();
  private readonly e1 = new THREE.Vector3();
  private readonly e2 = new THREE.Vector3();
  /** Average world albedo, for the integrating-sphere ambient term (and the ground's bounce). */
  readonly worldAlbedo = new THREE.Color(0.15, 0.18, 0.17);
  private readonly albedoNode = uniform(this.worldAlbedo);
  /** How the glow rises and falls (see GlowDef.pulse; null: it follows the veil). */
  readonly pulse: 'unified' | 'chaotic' | null;
  /** Unified glow: its level now (night/day … 1, a little more on a swell) and its day-ness (0 … 1). */
  readonly glowLevel = uniform(1);
  readonly glowVeil = uniform(1);
  /** Chaotic glow: the regions' flares. */
  readonly flares: GlowFlares | null;
  /** Seconds the glow has run (with the time scale). */
  private glowClock = FLARES_PREROLL;
  /** Chaotic glow: the lava's emission around a hollow-local position (see setLocalGlow). */
  private localGlow: ((local: N) => N) | null = null;

  constructor(
    readonly hollow: HollowDef,
    readonly veil: VeilDef,
    readonly atmosphere: AtmosphereDef | null,
    readonly glow: GlowDef | null = null,
  ) {
    const sun = hollow.sun;
    this.pulse = !sun && glow?.pulse ? glow.pulse : null;
    this.flares = this.pulse === 'chaotic' ? new GlowFlares(hollow.seed) : null;
    this.sunIrradiance = sun?.intensity ?? 0;
    if (sun) this.sunColor.value.setRGB(...sun.color);
    this.duskColor.value.setRGB(...veil.duskColor);
    this.veilCos.value = 1 - 2 * veil.litFraction;
    this.veilSoft.value = veil.softness;
    this.spin.set(...veil.spinAxis).normalize();
    const helper = Math.abs(this.spin.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    this.e1.crossVectors(helper, this.spin).normalize();
    this.e2.crossVectors(this.spin, this.e1).normalize();

    // Physically based point light: irradiance = I / d², so I = E_wall · R².
    this.sunLight = sun ? new THREE.PointLight(0xffffff, sun.intensity * hollow.radius ** 2, 0, 2) : null;

    this.ambient = new THREE.AmbientLight(0xffffff, 1);
    // (With a sun, attachSunColor adds the sunlit ground's bounce to this.)
    this.attachAmbient();
    this.update(0);
  }

  /** The bounce light's colour node for the scene: with a chaotic glow, the lava's own light too. */
  private attachAmbient(): void {
    const local = positionWorld.sub(this.centerRel);
    (this.ambient as unknown as { colorNode: N }).colorNode = this.ambientAt(local)
      .add(this.localGlowAt(local))
      .mul(this.dimmer);
  }

  /**
   * The light lava throws on the ground around it, from `emission`: the lava's emitted radiance
   * (luminance) around a hollow-local position, blurred over its region (the world panorama's
   * emission, which carries each region's flares). Where there is no lava there is no light,
   * and the shores of a flaring lake light up. Rebuilds the bounce light's node.
   */
  setLocalGlow(emission: (local: N) => N): void {
    this.localGlow = emission;
    this.attachAmbient();
  }

  private localGlowAt(local: N): N {
    if (!this.glow || !this.localGlow) return vec3(0);
    return vec3(...this.glow.color).mul(this.localGlow(local).mul(Math.PI * LOCAL_GLOW_GAIN));
  }

  /**
   * Bounce light (irradiance) at a hollow-local position. It is uniform over an ideal
   * sphere, but it reads better (and terrain occludes much of the lit far side near the
   * ground) if night dims to a cool twilight and daylight keeps the full bounce.
   */
  ambientAt(local: N): N {
    const veil = this.veil;
    const v = this.veilAt(normalize(local));
    const rim = float(1).sub(v.mul(2).sub(1).abs());
    const dayNight = mix(vec3(...(veil.nightAmbient ?? [0.16, 0.2, 0.34])), vec3(1, 1, 1), v);
    const tint = mix(
      dayNight,
      vec3(...(veil.rimAmbient ?? [1.25, 0.72, 0.45])),
      pow(rim, 1.5).mul(veil.rimAmbientStrength ?? 0.55),
    );
    return vec3(this.ambientColor as N).mul(tint);
  }

  /**
   * ambientAt on the CPU, for a unit hollow-local direction: the bounce light a figure
   * standing there gets (the hero, which is lit by its own lights).
   */
  ambientAtCpu(dir: THREE.Vector3, out: THREE.Color): THREE.Color {
    const veil = this.veil;
    const v = this.veilAtCpu(dir);
    const rim = (1 - Math.abs(2 * v - 1)) ** 1.5 * (veil.rimAmbientStrength ?? 0.55);
    const night = veil.nightAmbient ?? [0.16, 0.2, 0.34];
    const warm = veil.rimAmbient ?? [1.25, 0.72, 0.45];
    const tint = (k: number) => {
      const dayNight = night[k] + (1 - night[k]) * v;
      return dayNight + (warm[k] - dayNight) * rim;
    };
    const a = this.ambientColor.value;
    return out.setRGB(a.r * tint(0), a.g * tint(1), a.b * tint(2));
  }

  /**
   * All the daylight (irradiance) reaching a surface with normal `n` at a hollow-local
   * position, exactly as the hollow's lights deliver it to its ground: the bounce light (with
   * the sunlit ground's) plus the sun light. For surfaces outside the hollow's own lighting,
   * like a cave's first metres of rock, which must match the ground beside them.
   */
  daylightAt(local: N, n: N, withLocalGlow = false): N {
    const amb = withLocalGlow ? this.ambientAt(local).add(this.localGlowAt(local)) : this.ambientAt(local);
    if (!this.sunLight) return amb;
    const r = max(length(local), 1);
    const light = this.lightColorAt(local);
    // A point light at the centre: its colour node over r².
    const toSun = local.negate().div(r);
    return amb.add(this.groundBounce(local, n, light)).add(
      this.sunColorNode(light)
        .div(r.mul(r))
        .mul(max(dot(n, toSun), 0)),
    );
  }

  /** Optional cloud shadowing of sunlight at a hollow-local position (set before attachSunColor). */
  cloudShadow: ((local: N) => N) | null = null;
  /**
   * Optional shade of tree crowns at a render-space position (see CanopyShadow; set before
   * attachSunColor). It dims the sun light only, per fragment: not the air or the clouds.
   */
  canopyShadow: ((renderPos: N) => N) | null = null;

  /**
   * The sun light's colour node, from lightColorAt. A light's colorNode replaces its colour ×
   * intensity in three.js (`intensity` is then ignored), so it carries the intensity itself:
   * E_wall · R², which the point light's 1/d² turns back into E_wall at the ground. daylightAt
   * uses this same node, so the caves' first metres get exactly the ground's sunlight.
   */
  private sunColorNode(light: N): N {
    return light.mul(this.sunIrradiance * this.hollow.radius ** 2);
  }

  /**
   * Sunlight bounced off the ground nearby, on a surface with normal `n` (`light` is
   * lightColorAt there). A surface facing up sees the far wall, which the integrating-sphere
   * bounce light already covers. One turned sideways or down (a boulder's flank, a trunk, a
   * cliff) sees the ground around it instead, and by day that is sunlit and far brighter.
   */
  private groundBounce(local: N, n: N, light: N): N {
    const below = float(1)
      .sub(dot(n, normalize(local).negate()))
      .mul(0.5);
    return light.mul(vec3(this.albedoNode as N)).mul(below.mul(this.sunIrradiance));
  }

  /**
   * Build the sun light's per-fragment colour (veil, dusk tint, air, clouds) and add the
   * sunlight bounced off the ground to the bounce light.
   */
  attachSunColor(): void {
    if (!this.sunLight) return;
    const local = positionWorld.sub(this.centerRel);
    // One node for both lights: the shader computes it (clouds and all) once per fragment.
    const light = this.lightColorAt(local);
    const sun = this.canopyShadow ? light.mul(this.canopyShadow(positionWorld)) : light;
    (this.sunLight as unknown as { colorNode: N }).colorNode = this.sunColorNode(sun).mul(this.dimmer);
    (this.ambient as unknown as { colorNode: N }).colorNode = this.ambientAt(local)
      .add(this.groundBounce(local, normalWorld, light))
      .mul(this.dimmer);
  }

  /**
   * Veil visibility (0 night … 1 day) for a hollow-local direction. In a glowing world it is the
   * glow's own day: the same everywhere for a unified glow, the region's flare for a chaotic one.
   */
  veilAt(dir: N): N {
    if (this.pulse === 'unified') return this.glowVeil;
    if (this.flares) return clamp(this.flares.sample(dir), 0, 1);
    return smoothstep(
      this.veilCos.sub(this.veilSoft),
      this.veilCos.add(this.veilSoft),
      dot(dir, this.veilAxis),
    );
  }

  /** Warm tint through the veil's rim: strongest at mid-penumbra. */
  tintAt(v: N): N {
    const rim = float(1).sub(v.mul(2).sub(1).abs());
    return mix(vec3(this.sunColor as N), vec3(this.duskColor as N), pow(rim, 0.7).mul(0.85));
  }

  /** Sun transmittance from the centre out to radius r (analytic, exponential air). */
  sunTransmittance(r: N): N {
    const a = this.atmosphere;
    if (!a) return vec3(1);
    const R = this.hollow.radius;
    const tauR = vec3(...a.rayleigh)
      .mul(a.rayleighHeight)
      .mul(exp(r.sub(R).div(a.rayleighHeight)).sub(Math.exp(-R / a.rayleighHeight)));
    const mieExt = a.mie * (1 + a.mieAbsorption);
    const tauM = float(mieExt * a.mieHeight).mul(
      exp(r.sub(R).div(a.mieHeight)).sub(Math.exp(-R / a.mieHeight)),
    );
    return exp(tauR.add(tauM).negate());
  }

  /** Colour multiplier of sunlight arriving at a hollow-local position. */
  lightColorAt(local: N): N {
    const r = max(length(local), 1);
    const v = this.veilAt(normalize(local));
    const lit = this.tintAt(v).mul(v).mul(this.sunTransmittance(r));
    return this.cloudShadow ? lit.mul(this.cloudShadow(local)) : lit;
  }

  /**
   * The sunlight (irradiance, linear RGB) arriving `r` from the centre in hollow-local unit
   * direction `dir`, on the CPU: what the sun light delivers there (lightColorAt × E_wall ·
   * R²/r²), without the clouds' shadow. For figures lit by their own lights (the hero).
   */
  sunAtCpu(dir: THREE.Vector3, r: number, out: THREE.Color): THREE.Color {
    if (!this.sunLight) return out.setRGB(0, 0, 0);
    const v = this.veilAtCpu(dir);
    const rim = (1 - Math.abs(2 * v - 1)) ** 0.7 * 0.85;
    const s = this.sunColor.value;
    const d = this.duskColor.value;
    const t = this.cpuSunTransmittance(r);
    const R = this.hollow.radius;
    const e = this.sunIrradiance * v * (R / Math.max(r, 1)) ** 2;
    return out.setRGB(
      (s.r + (d.r - s.r) * rim) * t[0] * e,
      (s.g + (d.g - s.g) * rim) * t[1] * e,
      (s.b + (d.b - s.b) * rim) * t[2] * e,
    );
  }

  /** Veil visibility on the CPU (for exposure and HUD). */
  veilAtCpu(dir: THREE.Vector3): number {
    if (this.pulse === 'unified') return this.glowVeil.value;
    if (this.flares) return Math.min(1, this.flares.valueAt(dir));
    const d = dir.dot(this.veilAxis.value);
    const lo = this.veilCos.value - this.veilSoft.value;
    const hi = this.veilCos.value + this.veilSoft.value;
    const t = Math.min(1, Math.max(0, (d - lo) / (hi - lo)));
    return t * t * (3 - 2 * t);
  }

  /**
   * Integrating-sphere ambient: every point of a sphere's inner wall sees the rest of
   * the wall equally, so bounce light is uniform: M = ρ·E_avg / (1 − ρ).
   */
  ambientIrradiance(out: THREE.Color): THREE.Color {
    const a = this.worldAlbedo;
    const lit = this.veil.litFraction;
    out.setRGB(0, 0, 0);
    if (this.sunIrradiance > 0) {
      const eAvg = this.sunIrradiance * lit;
      const t = this.atmosphere ? this.cpuSunTransmittance(this.hollow.radius) : [1, 1, 1];
      out.setRGB(
        ((a.r * eAvg) / (1 - a.r)) * t[0],
        ((a.g * eAvg) / (1 - a.g)) * t[1],
        ((a.b * eAvg) / (1 - a.b)) * t[2],
      );
      // Half-desaturate and lean toward sky blue: the air between us and the walls
      // scatters blue into the bounce light, and it keeps shadows from going green.
      const lum = 0.2126 * out.r + 0.7152 * out.g + 0.0722 * out.b;
      out.setRGB(
        (lum + (out.r - lum) * 0.5) * 0.92,
        (lum + (out.g - lum) * 0.5) * 0.98,
        (lum + (out.b - lum) * 0.5) * 1.12,
      );
    }
    if (this.glow) {
      // Self-lit walls: emitted light plus all its bounces, E = M_avg / (1 − ρ).
      const g = this.glow;
      const m =
        this.pulse === 'unified'
          ? g.day * this.glowLevel.value
          : this.flares
            ? g.night + (g.day - g.night) * this.flares.average
            : g.day * lit + g.night * (1 - lit);
      out.r += (g.color[0] * m) / (1 - a.r);
      out.g += (g.color[1] * m) / (1 - a.g);
      out.b += (g.color[2] * m) / (1 - a.b);
    }
    return out;
  }

  /**
   * Glow multiplier at a hollow-local direction (the night level … 1, more in a swell or an
   * eruption), as a node: what glowing ground and plants multiply their light by.
   */
  glowAt(dir: N): N {
    const g = this.glow;
    if (!g) return float(0);
    if (this.pulse === 'unified') return this.glowLevel;
    const low = float(g.night / g.day);
    if (this.flares) return mix(low, float(1), this.flares.sample(dir));
    return mix(low, float(1), this.veilAt(dir));
  }

  cpuSunTransmittance(r: number): [number, number, number] {
    const a = this.atmosphere;
    if (!a) return [1, 1, 1];
    const R = this.hollow.radius;
    const fR = a.rayleighHeight * (Math.exp((r - R) / a.rayleighHeight) - Math.exp(-R / a.rayleighHeight));
    const fM =
      a.mie *
      (1 + a.mieAbsorption) *
      a.mieHeight *
      (Math.exp((r - R) / a.mieHeight) - Math.exp(-R / a.mieHeight));
    return [
      Math.exp(-(a.rayleigh[0] * fR + fM)),
      Math.exp(-(a.rayleigh[1] * fR + fM)),
      Math.exp(-(a.rayleigh[2] * fR + fM)),
    ];
  }

  /** Advance the day. */
  update(dt: number): void {
    this.time = (this.time + (dt * this.timeScale) / this.veil.dayLength) % 1;
    const ang = this.time * Math.PI * 2;
    _tmp.copy(this.e1).multiplyScalar(Math.cos(ang)).addScaledVector(this.e2, Math.sin(ang));
    this.veilAxis.value.copy(_tmp);
    this.glowClock += dt * this.timeScale;
    this.updateGlow();
    this.ambientIrradiance(this.ambientColor.value);
  }

  private updateGlow(): void {
    const g = this.glow;
    if (this.pulse === 'unified' && g) {
      const s = glowBreath(this.time);
      const swell = 1 + RIPPLE * s * Math.sin((this.glowClock / RIPPLE_PERIOD) * Math.PI * 2);
      const low = g.night / g.day;
      this.glowLevel.value = (low + (1 - low) * s) * swell;
      this.glowVeil.value = s;
    } else if (this.flares) {
      this.flares.update(this.glowClock);
    }
  }

  /** Day phase at which the veil's axis points closest to `dir` (local noon; everywhere at once for a glow of its own). */
  private noonPhase(dir: THREE.Vector3): number {
    if (this.pulse) return 0;
    const a = Math.atan2(dir.dot(this.e2), dir.dot(this.e1)) / (Math.PI * 2);
    return (a + 1) % 1;
  }

  /** Local clock time at a hollow-local direction, in hours (12 = noon, 0 = midnight). */
  localHour(dir: THREE.Vector3): number {
    return ((((this.time - this.noonPhase(dir)) * 24 + 12) % 24) + 24) % 24;
  }

  /** Turn the veil so it is `hour` o'clock at a hollow-local direction. */
  setLocalHour(dir: THREE.Vector3, hour: number): void {
    this.time = (((this.noonPhase(dir) + (hour - 12) / 24) % 1) + 1) % 1;
    this.update(0);
  }

  /** Local "time of day" label for a hollow-local direction, e.g. for the HUD. */
  describe(dir: THREE.Vector3): string {
    if (this.flares) {
      // Lava keeps no clock: each region flares when it will.
      const f = this.flares.valueAt(dir);
      return f > 0.6 ? 'lava flaring' : f > 0.15 ? 'lava stirring' : 'lava quiet';
    }
    const h = this.localHour(dir);
    const clock = `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.floor((h % 1) * 60)).padStart(2, '0')}`;
    const v = this.veilAtCpu(dir);
    if (this.pulse === 'unified') {
      if (v > 0.97) return `glowing ${clock}`;
      if (v < 0.03) return `dark ${clock}`;
      return `${h < 12 ? 'brightening' : 'dimming'} ${clock}`;
    }
    if (v > 0.97) return `day ${clock}`;
    if (v < 0.03) return `night ${clock}`;
    return `${h < 12 ? 'dawn' : 'dusk'} ${clock}`;
  }
}
